<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'ContractServiceFees',
    path: '/contract-service-fees',
    component: 'ContractServiceFees',
    title: () => gettext('Contract service fees'),
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'ContractServiceFees',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const selectedItems = ref([]);
  const dsParams = ref({
    skip_archived: true,
  });
  const ds = ref(null);
  const gridName = ref('contractServiceFees');
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_2248226175642056_columnDefaults = computed(() => ({
    sortable: true,
    filterable: true,
    resizable: true,
    selectable: true,
  }));
  const b_2248226175642056_columns = computed(() => [
    {
      field: 'contract_revision',
      name: $fn.value.pgettext('Contract', 'Contract number'),
      params: {
        sortPath: 'contract_revision.contract_number',
      },
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        formatters: [
          {
            name: 'getvalue',
            opts: {
              field: 'contract_revision.contract_number',
              source: 'row',
            },
          },
          {
            name: 'routeLink',
            to: 'contract',
            navigateOnIconClick: true,
            skipExport: true,
            params: {
              id: 'contract_id',
            },
          },
        ],
      },
    },
    {
      id: 'contract_type',
      field: 'contract_revision',
      name: $fn.value.pgettext('Contracts', 'Type'),
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        formatters: [
          {
            name: 'getvalue',
            opts: {
              field: 'contract_type',
              source: 'value',
            },
          },
          {
            name: 'gettext',
            context: 'ContractTypes',
            conversion: 'StartCase',
            filterInput: true,
          },
          {
            name: 'badge',
            variantMapSource: 'inputValue',
            variantmap: {
              rental: 'contract-type--rental',
              vendor: 'contract-type--vendor',
              rental_agreement: 'contract-type--rental_agreement',
              service: 'contract-type--service',
              parking: 'contract-type--parking',
              unknown: 'contract-type--unknown',
            },
          },
        ],
      },
    },
    {
      id: 'contract_status',
      field: 'contract_revision',
      name: $fn.value.pgettext('Contracts', 'Status'),
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        formatters: [
          {
            name: 'getvalue',
            opts: {
              field: 'status',
              source: 'value',
            },
          },
          {
            name: 'gettext',
            context: 'ContractStatus',
            conversion: 'StartCase',
            filterInput: true,
          },
          {
            name: 'badge',
            variantMapSource: 'fieldValue',
            variantmap: {
              draft: 'light',
              approved: 'success',
              signed: 'success',
              active: 'primary',
              ended: 'warning',
              terminated: 'warning',
              rescinded: 'danger',
              archived: 'light',
            },
          },
        ],
      },
    },
    {
      field: 'rental',
      name: $fn.value.pgettext('ContractServiceFees', 'Rental'),
      type: 'command',
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    <strong>{{ $value.name }}</strong>\n\
    {{~#if $value.house_number }} | {{ $value.house_number }}{{/if }}\n\
    {{~#if $value.area }} ({{$value.area}}{{$value.unit_of_area}}){{/if }}\n\
  {{~/if }}\n\
</span>\n\
',
          },
          {
            name: 'routeLink',
            to: 'rental',
            navigateOnIconClick: true,
            params: {
              id: 'rental_id',
            },
          },
        ],
      },
    },
    {
      field: 'company',
      name: $fn.value.pgettext('Contract', 'Company'),
      params: {
        sortPath: 'company.name',
      },
      type: 'command',
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    <strong title="{{ $value.name }}">{{ $value.code }}</strong>\n\
  {{~/if }}\n\
</span>\n\
',
          },
          {
            name: 'routeLink',
            to: 'company',
            navigateOnIconClick: true,
            params: {
              id: 'company.id',
            },
          },
        ],
      },
    },
    {
      field: 'counterparty',
      name: $fn.value.pgettext('Contract', 'Counterparty'),
      params: {
        sortPath: 'counterparty.name',
      },
      type: 'command',
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    <strong title="{{ $value.name }}">{{ $value.code }}</strong>\n\
  {{~/if }}\n\
</span>\n\
',
          },
          {
            name: 'routeLink',
            to: 'company',
            navigateOnIconClick: true,
            params: {
              id: 'counterparty.id',
            },
          },
        ],
      },
    },
    {
      field: 'valid_from',
      name: $fn.value.pgettext('Contracts', 'Valid From'),
      formatter: {
        name: 'date',
      },
    },
    {
      field: 'valid_to',
      name: $fn.value.pgettext('Contracts', 'Valid To'),
      formatter: {
        name: 'date',
      },
    },
    {
      field: 'next_indexation_date',
      name: $fn.value.pgettext('Contracts', 'Next Indexation Date'),
      formatter: {
        name: 'date',
      },
    },
    {
      field: 'name',
      name: $fn.value.pgettext('ContractService', 'Name'),
      type: 'command',
    },
    {
      field: 'amount',
      name: $fn.value.pgettext('ContractService', 'Amount'),
      type: 'command',
      textAlign: 'right',
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if amount }}\n\
    {{ number amount }} {{ currency }}\n\
  {{~/if }}\n\
</span>\n\
',
          },
        ],
      },
    },
    {
      field: 'billable_amount',
      name: $fn.value.pgettext('ContractService', 'Total'),
      type: 'command',
      textAlign: 'right',
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if amount }}\n\
    {{ number billable_amount }} {{ currency }}\n\
    {{~#if (neq currency billing_currency) }}\n\
      (→ {{ billing_currency }})\n\
    {{~/if }}\n\
  {{~/if }}\n\
</span>\n\
',
          },
          {
            name: 'clicksink',
          },
        ],
      },
    },
    {
      field: 'unit',
      name: $fn.value.pgettext('ContractServiceFee', 'Unit'),
    },
    {
      field: 'quantity',
      name: $fn.value.pgettext('ContractService', 'Quantity'),
      type: 'command',
      textAlign: 'right',
      formatter: {
        name: 'template',
        engine: 'hb',
        format:
          '<span>\n\
  {{~#if quantity }}\n\
    {{ number quantity }} {{ unit_of_quantity }}\n\
  {{~/if }}\n\
</span>\n\
',
      },
    },
    {
      field: 'inflation_indexed',
      name: $fn.value.pgettext('ContractService', 'Inflation indexed'),
      formatter: 'checkbox',
    },
    {
      field: 'transaction_notes',
      name: $fn.value.pgettext('ContractService', 'Invoice notes'),
      formatter: {
        name: 'join',
        separator: ', ',
      },
    },
    {
      field: 'billing_category',
      name: $fn.value.pgettext('ContractService', 'Billing category'),
    },
    {
      field: 'invoice_group_id',
      name: $fn.value.pgettext('ContractService', 'Invoice group'),
    },
    {
      field: 'description',
      name: $fn.value.pgettext('ContractService', 'Description'),
    },
    {
      field: 'intermediated_service',
      name: $fn.value.pgettext('ContractService', 'Intermediated service'),
      formatter: 'checkbox',
    },
    {
      field: 'created_at',
      name: $fn.value.pgettext('BrowserColumn', 'Create time'),
      formatter: 'datetime',
    },
  ]);

  const $data = computed(() => ({
    selectedItems: selectedItems.value,
    dsParams: dsParams.value,
    ds: ds.value,
    gridName: gridName.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_2248226175642056_columnDefaults: b_2248226175642056_columnDefaults.value,
    b_2248226175642056_columns: b_2248226175642056_columns.value,
  }));

  const $$load_ds = async () => {
    return $fn.value.slickDataSource('integro_api/list_contract_service_fee', {
      parameters: () => ({ ...dsParams.value, date: $fn.value.today() }),
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'selectedItems' },
      {
        name: 'dsParams',
        cache: { type: 'local', key: 'ContractServiceFees.dsParams' },
      },
      { name: 'ds' },
      { name: 'gridName' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_2248226175642056_columnDefaults', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_columns', nodeId: '2248226175642056' },
    ],
    loaders: { ds: $$load_ds },
    ux: {},
    dataRefs: {
      selectedItems,
      dsParams,
      ds,
      gridName,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_2248226175642056_columnDefaults,
      b_2248226175642056_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.gettext('Contract service fees'),
      icon: $config.value.contract.icon,
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_zi6jehp9d_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2248226175642056_setData_e5tbo0otr_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2248226175642056_showModal_tu1ndjygh_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ContractService',
      props: {
        modal: true,
        oid: $event.data.data.contract_service_id,
      },
    };
  };
  const inst_evh_2248226175642056_command_mk7osxwbd_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'name';
  };
  const act_2248226175642056_showModal_j4ugjcx3h_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ContractServiceFee',
      props: {
        modal: true,
        oid: $event.data.data.id,
      },
    };
  };
  const inst_evh_2248226175642056_command_gg96ilgqg_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'billable_amount';
  };
  const act_7172922356531116_reloadSlickgrid_svbcvwydy_getActionArgs = async (
    $event
  ) => {
    return {
      grid: gridName.value,
    };
  };
  const inst_evh_7172922356531116_close_nntey1kvk_getWhen = async ($event) => {
    return $event.data.modalResult.ok;
  };
  const act_7172922356531116_showModal_3btesa2c4_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'BulkContractServiceFeeEditor',
      props: {
        items: selectedItems.value,
      },
    };
  };
  const act_5246287230194476_reloadSlickgrid_385ag0oru_getActionArgs = async (
    $event
  ) => {
    return {
      grid: gridName.value,
    };
  };
  const act_5246287230194476_request_m535x2vls_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'bulk_delete_contract_service_fee',
      data: {
        items: _.map(selectedItems.value, (p) => _.pick(p, ['id', 'etag'])),
      },
    };
  };
  const act_6939677778299611_reloadSlickgrid_3ssmbc97g_getActionArgs = async (
    $event
  ) => {
    return {
      grid: gridName.value,
    };
  };
  const inst_evh_6939677778299611_close_s0235o2fc_getWhen = async ($event) => {
    return $event.data.modalResult.ok;
  };
  const act_6939677778299611_showModal_33mhvn3wh_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'BulkIndexContractServiceFee',
      props: {
        items: selectedItems.value,
      },
    };
  };

  const act_7315092382398562_closeModal_zi6jehp9d =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_zi6jehp9d_getActionArgs,
    });
  const inst_evh_7315092382398562_close_l4r6slwlg =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_zi6jehp9d,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_l4r6slwlg],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_8y7sss2sj =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_rl8qscczr =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_8y7sss2sj,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_rl8qscczr],
    event: 'reload',
  });
  const act_2248226175642056_setData_e5tbo0otr = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_e5tbo0otr_getActionArgs,
  });
  const inst_evh_2248226175642056_selectedRowsChanged_2ryvib2n4 =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_e5tbo0otr,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_2248226175642056_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_selectedRowsChanged_2ryvib2n4],
      event: 'selectedRowsChanged',
    });
  const act_2248226175642056_showModal_tu1ndjygh =
    new actionTypes.ShowModalAction({
      actionArgs: act_2248226175642056_showModal_tu1ndjygh_getActionArgs,
    });
  const inst_evh_2248226175642056_command_mk7osxwbd =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_showModal_tu1ndjygh,
      event: 'command',
      when: inst_evh_2248226175642056_command_mk7osxwbd_getWhen,
      displayName: 'showModal',
    });
  const act_2248226175642056_showModal_j4ugjcx3h =
    new actionTypes.ShowModalAction({
      actionArgs: act_2248226175642056_showModal_j4ugjcx3h_getActionArgs,
    });
  const inst_evh_2248226175642056_command_gg96ilgqg =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_showModal_j4ugjcx3h,
      event: 'command',
      when: inst_evh_2248226175642056_command_gg96ilgqg_getWhen,
      displayName: 'showModal',
    });
  const inst_evh_2248226175642056_command = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_2248226175642056_command_mk7osxwbd,
      inst_evh_2248226175642056_command_gg96ilgqg,
    ],
    event: 'command',
  });
  const act_7172922356531116_reloadSlickgrid_svbcvwydy =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_7172922356531116_reloadSlickgrid_svbcvwydy_getActionArgs,
      displayName: 'reload',
    });
  const inst_evh_7172922356531116_close_nntey1kvk =
    new actionTypes.EventHandlerImpl({
      action: act_7172922356531116_reloadSlickgrid_svbcvwydy,
      event: 'close',
      when: inst_evh_7172922356531116_close_nntey1kvk_getWhen,
      displayName: 'reload',
    });
  const act_7172922356531116_showModal_3btesa2c4 =
    new actionTypes.ShowModalAction({
      actionArgs: act_7172922356531116_showModal_3btesa2c4_getActionArgs,
      events: [inst_evh_7172922356531116_close_nntey1kvk],
    });
  const inst_evh_7172922356531116_click_nb4vnono1 =
    new actionTypes.EventHandlerImpl({
      action: act_7172922356531116_showModal_3btesa2c4,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_7172922356531116_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7172922356531116_click_nb4vnono1],
    event: 'click',
  });
  const act_5246287230194476_reloadSlickgrid_385ag0oru =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_5246287230194476_reloadSlickgrid_385ag0oru_getActionArgs,
      displayName: 'reload',
    });
  const inst_evh_5246287230194476_success_tmoe6aw51 =
    new actionTypes.EventHandlerImpl({
      action: act_5246287230194476_reloadSlickgrid_385ag0oru,
      event: 'success',
      displayName: 'reload',
    });
  const act_5246287230194476_request_m535x2vls = new actionTypes.RequestAction({
    actionArgs: act_5246287230194476_request_m535x2vls_getActionArgs,
    displayName: 'bulkDelete',
    events: [inst_evh_5246287230194476_success_tmoe6aw51],
  });
  const inst_evh_5246287230194476_click_ngtfg3035 =
    new actionTypes.EventHandlerImpl({
      action: act_5246287230194476_request_m535x2vls,
      event: 'click',
      displayName: 'bulkDelete',
    });
  const inst_evh_5246287230194476_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5246287230194476_click_ngtfg3035],
    event: 'click',
  });
  const act_6939677778299611_reloadSlickgrid_3ssmbc97g =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_6939677778299611_reloadSlickgrid_3ssmbc97g_getActionArgs,
      displayName: 'reload',
    });
  const inst_evh_6939677778299611_close_s0235o2fc =
    new actionTypes.EventHandlerImpl({
      action: act_6939677778299611_reloadSlickgrid_3ssmbc97g,
      event: 'close',
      when: inst_evh_6939677778299611_close_s0235o2fc_getWhen,
      displayName: 'reload',
    });
  const act_6939677778299611_showModal_33mhvn3wh =
    new actionTypes.ShowModalAction({
      actionArgs: act_6939677778299611_showModal_33mhvn3wh_getActionArgs,
      events: [inst_evh_6939677778299611_close_s0235o2fc],
    });
  const inst_evh_6939677778299611_click_kgsl25gms =
    new actionTypes.EventHandlerImpl({
      action: act_6939677778299611_showModal_33mhvn3wh,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_6939677778299611_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6939677778299611_click_kgsl25gms],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_close_l4r6slwlg,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_rl8qscczr,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_selectedRowsChanged_2ryvib2n4,
    inst_evh_2248226175642056_selectedRowsChanged,
    inst_evh_2248226175642056_command_mk7osxwbd,
    inst_evh_2248226175642056_command_gg96ilgqg,
    inst_evh_2248226175642056_command,
    inst_evh_7172922356531116_close_nntey1kvk,
    reload: inst_evh_7172922356531116_close_nntey1kvk,
    inst_evh_7172922356531116_click_nb4vnono1,
    inst_evh_7172922356531116_click,
    inst_evh_5246287230194476_success_tmoe6aw51,
    inst_evh_5246287230194476_click_ngtfg3035,
    bulkDelete: inst_evh_5246287230194476_click_ngtfg3035,
    inst_evh_5246287230194476_click,
    inst_evh_6939677778299611_close_s0235o2fc,
    inst_evh_6939677778299611_click_kgsl25gms,
    inst_evh_6939677778299611_click,
  };

  const evh_7315092382398562_close_l4r6slwlg = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_l4r6slwlg.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_rl8qscczr = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_rl8qscczr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged_2ryvib2n4 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged_2ryvib2n4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command_mk7osxwbd = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command_mk7osxwbd.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command_gg96ilgqg = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command_gg96ilgqg.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7172922356531116_close_nntey1kvk = async (event, scope) => {
    return await actions.inst_evh_7172922356531116_close_nntey1kvk.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7172922356531116_click_nb4vnono1 = async (event, scope) => {
    return await actions.inst_evh_7172922356531116_click_nb4vnono1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7172922356531116_click = async (event, scope) => {
    return await actions.inst_evh_7172922356531116_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5246287230194476_success_tmoe6aw51 = async (event, scope) => {
    return await actions.inst_evh_5246287230194476_success_tmoe6aw51.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5246287230194476_click_ngtfg3035 = async (event, scope) => {
    return await actions.inst_evh_5246287230194476_click_ngtfg3035.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5246287230194476_click = async (event, scope) => {
    return await actions.inst_evh_5246287230194476_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6939677778299611_close_s0235o2fc = async (event, scope) => {
    return await actions.inst_evh_6939677778299611_close_s0235o2fc.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6939677778299611_click_kgsl25gms = async (event, scope) => {
    return await actions.inst_evh_6939677778299611_click_kgsl25gms.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6939677778299611_click = async (event, scope) => {
    return await actions.inst_evh_6939677778299611_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="ContractServiceFees"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <VueSlickgrid
      :name="gridName"
      :headerTitle="pgettext('ContractServiceFees', 'Contract service fees')"
      :items="ds"
      checkbox-row-selection
      export-to-csv
      :exportToCsvFilename="pgettext('filename', 'contract-service-fees')"
      :columnDefaults="b_2248226175642056_columnDefaults"
      :columns="b_2248226175642056_columns"
      class="page--grid"
      ref="grid"
      @selectedRowsChanged="
        evh_2248226175642056_selectedRowsChanged($event, {})
      "
      @command="evh_2248226175642056_command($event, {})"
    >
      <template #toolbar>
        <div class="flex-grow-1 d-flex gap-2 align-items-center">
          <div>
            <ItpFormCheckbox
              name="skip_archived"
              :label="pgettext('ContractServiceFees', 'Skip archived items')"
              inline
              v-model="dsParams.skip_archived"
              :value="dsParams.skip_archived"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (dsParams.skip_archived =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormCheckbox>
          </div>
          <div class="border-start ps-3 d-flex gap-2">
            <ItpButton
              :disabled="!selectedItems || !selectedItems.length"
              :icon="$config.contract.createContractIcon"
              :text="pgettext('ContractServiceFees', 'Bulk edit...')"
              size="sm"
              variant="light"
              @click="evh_7172922356531116_click($event, {})"
            >
            </ItpButton>
            <ItpButton
              :disabled="!selectedItems || !selectedItems.length"
              :icon="$config.deleteIcon"
              :text="pgettext('ContractServiceFees', 'Delete')"
              :title="
                pgettext('ContractServiceFees', 'Delete the selected items')
              "
              size="sm"
              variant="light"
              :spinning="actions.bulkDelete.isRunning"
              @click="evh_5246287230194476_click($event, {})"
            >
            </ItpButton>
            <ItpButton
              :disabled="!selectedItems || !selectedItems.length"
              :icon="$config.priceIndexingIcon"
              :text="pgettext('ContractServiceFees', 'Indexing...')"
              size="sm"
              variant="light"
              @click="evh_6939677778299611_click($event, {})"
            >
            </ItpButton>
          </div>
          <PageHelp path="/contracts/contracts.html"> </PageHelp>
        </div>
      </template>
    </VueSlickgrid>
  </ItpPage>
</template>
